import { UserContext } from "./store/Models/UserContext";
import { IStringLocalizer } from "./utilities/IStringLocalizer";

// ===============================================================================

export interface TowState {
    taskId: string,
    type: string,
    jsonState: string
}

// ===============================================================================

export interface ThreeEAppStripConfiguration {
    enabled: boolean;
}

// ===============================================================================

export interface ToxnotConfiguration {
    toxnotWebAppUrlEndpoint: string;
    surveyQuestionBankEnabled: boolean;
    googleDeclarePageReadOnly: boolean;
    maxFileUploadSizeInBytes: number;
    teamSeatTimeoutInMinutes: number;
    largeBomRowCountThreshold: number;
    thirdPartyVerificationEnabled: boolean;
    declare2EnabledForAll: boolean;
    isProductionServer: boolean;
    isDevelopmentServer: boolean;
    isBetaServer: boolean;
    isUatServer: boolean;
    dataRefreshStartDate: string;
    inventoryLockTimeoutInSeconds: number;
    inventoryLockPollingFrequencyInSeconds: number;
    inTestingMode: boolean;
    newsBffBaseUrl: string;
    newsOpenedArticleUrl: string;
    newsNewsletterArchiveUrl: string;
    newsSearchBaseUrl: string;
    regulationsBffBaseUrl: string;
    regulationsApiBaseUrl: string;
    exchangeFreeUsersAccountId: number;
    aiAssistantComponentsBaseUrl: string;
    aiAssistantBffBaseUrl: string;
    aiAssistantGenerateTitleApiBaseUrl: string;
    aiAssistantApiBaseUrl: string;
    aiAssistantWsBackendUrl: string;
}

// ===============================================================================

//
// Application insights object, included via javascript snippet in index.html.
// Used to send events to Application Insights in Azure.
// We use these events to track metrics like "how many users sent a survey" or "how many users created a declare
// label."
//

declare const appInsights: any;

//
// these localization resource maps are included by index.html and will exist globally
//

declare const localization_englishStringToCustomKeyMap: { [englishString: string]: string };
declare const toxnotCoreContent_de: { [defaultString: string]: string };
declare const toxnotCoreContent_es: { [defaultString: string]: string };
declare const toxnotCoreContent_fr: { [defaultString: string]: string };
declare const toxnotCoreContent_it: { [defaultString: string]: string };
declare const toxnotCoreContent_ja: { [defaultString: string]: string };
declare const toxnotCoreContent_pt: { [defaultString: string]: string };
declare const toxnotCoreContent_th: { [defaultString: string]: string };
declare const toxnotCoreContent_zh: { [defaultString: string]: string };

// ===============================================================================

/**
 * Services and data used by all views
 */
export class AppServices implements IStringLocalizer {
    public readonly userContext: Readonly<UserContext> | null;

    public readonly config: Readonly<ToxnotConfiguration>;

    public readonly threeEAppStrip: Readonly<ThreeEAppStripConfiguration>;

    public readonly towState: TowState | null;

    private readonly defaultCultureName: string = "en";

    private selectedCultureName: string = "en";

    private readonly _localizationResourceLookupTable: Readonly<{
        [cultureName: string]: { [defaultString: string]: string };
    }>;


    // ===============================================================================

    public constructor(
        userContext: Readonly<UserContext> | null,
        towState: TowState | null,
        config: Readonly<ToxnotConfiguration>,
        threeEAppStrip: Readonly<ThreeEAppStripConfiguration>) {
        this.userContext = userContext;
        this.towState = towState;
        this.threeEAppStrip = threeEAppStrip;

        this._localizationResourceLookupTable = {
            "de": toxnotCoreContent_de,
            "es": toxnotCoreContent_es,
            "fr": toxnotCoreContent_fr,
            "it": toxnotCoreContent_it,
            "ja": toxnotCoreContent_ja,
            "pt": toxnotCoreContent_pt,
            "th": toxnotCoreContent_th,
            "zh": toxnotCoreContent_zh
        };

        this.config = config;

        if (userContext !== null) {
            this.setUserCultureName(userContext.cultureName);

            const applicationInsightsUserEmailAddress = userContext.emailAddress;
            const applicationInsightsUserActiveTeamName = userContext.teamName;
            const encodedApplicationInsightsUserId = applicationInsightsUserEmailAddress.replace(/[,;=| ]+/g, "_");
            const encodedApplicationInsightsAccountId = applicationInsightsUserActiveTeamName.replace(/[,;=| ]+/g, "_");

            appInsights.setAuthenticatedUserContext(encodedApplicationInsightsUserId, encodedApplicationInsightsAccountId);
            var telemetryInitializer = (envelope: any) => {
                envelope.tags["UserId"] = userContext.userId;
                envelope.tags["EmailAddress"] = userContext.emailAddress;
                envelope.tags["TeamOwnerId"] = userContext.teamOwnerId;
                envelope.tags["TeamName"] = userContext.teamName;
            }
            appInsights.addTelemetryInitializer(telemetryInitializer);

        }
    }

    // ===============================================================================

    /**
     * Localizes a given english string into the given culture.
     * 
     * @param cultureName The culture to localize s to
     * @param s The string to localize
     * 
     * @returns The translated version of s
     */
    public localizeStringToCultureName(cultureName: string, s: string): string {
        const cultureResource = this._localizationResourceLookupTable[cultureName];

        if (cultureResource === undefined) {
            return s;
        }

        const customKey = localization_englishStringToCustomKeyMap[s];

        const localizedString = cultureResource[customKey ?? s];

        if (localizedString === undefined) {
            return s;
        }

        return localizedString;
    }

    // ===============================================================================

    /**
     * Localizes a given english string into the language currently selected
     * by the user.
     * 
     * @param s The string to localize
     * 
     * @returns The translated version of s
     */
    public localizeString(s: string): string {        
        return this.localizeStringToCultureName(this.selectedCultureName, s);
    }

    // ===============================================================================

    /**
     * Returns the user's culture name or default culture name.
     */
    public getUserCultureNameOrDefault() {
        return this.userContext?.cultureName ?? this.defaultCultureName;
    }

    // ===============================================================================

    /**
     * Returns the user's culture name or default culture name.
     */
    public setUserCultureName(lang: string) {
        this.selectedCultureName = lang;
    }

    // ===============================================================================

    /**
     * Localizes a given english string into the given culture name,
     * substituting args into the string as a template.
     * 
     * @param cultureName The culture to localize s to
     * @param formatString The string to localize, of the form "content {0} content {1} ..."
     * where the ith element in args is substituted into "{i}"
     * 
     * @returns The translated version of formatString
     */
    public localizeStringToCultureNameFormat(cultureName: string, formatString: string, ...args: any[]): string {
        let localizedFormatString = this.localizeStringToCultureName(cultureName, formatString);

        let i = 0;
        let startPlaceholder = -1;

        let ret = "";

        while (i < localizedFormatString.length) {
            let substitution: string | null = null;

            if (localizedFormatString[i] === '{') {
                if (startPlaceholder !== -1) {
                    startPlaceholder = -1;
                }
                else {
                    startPlaceholder = i;
                }
            }
            else if (localizedFormatString[i] === '}') {
                if (startPlaceholder !== -1) {
                    const index = parseInt(localizedFormatString.substr(startPlaceholder + 1, i - startPlaceholder - 1));

                    substitution = args[index].toString() ?? "";

                    startPlaceholder = -1;
                }
            }

            if (substitution !== null) {
                ret += substitution;
            }
            else if (startPlaceholder === -1) {
                ret += localizedFormatString[i];
            }

            ++i;
        }

        return ret;
    }

    // ===============================================================================

    /**
     * Localizes a given english string into the language currently selected
     * by the user, substituting args into the string as a template.
     * 
     * @param formatString The string to localize, of the form "content {0} content {1} ..."
     * where the ith element in args is substituted into "{i}"
     * 
     * @returns The translated version of formatString
     */
    public localizeStringFormat(formatString: string, ...args: any[]): string {
        return this.localizeStringToCultureNameFormat(this.getUserCultureNameOrDefault(), formatString, ...args);
    }

    // ===============================================================================

    public static dupe(appServices: AppServices, towState: TowState | null, config: ToxnotConfiguration) {
        return new AppServices(appServices.userContext, towState, config, appServices.threeEAppStrip);
    }

    // ===============================================================================

    public displayApiErrorMessage(localizedMessage?: string) {
        alert(localizedMessage ?? this.localizeString("Sorry, there was a problem accessing the service."));
    }

    // ===============================================================================

    /**
     * Returns the display name of the given culture in the default culture.
     * e.g. if the default culture is English, and the cultureName is "es", then
     * this returns "Spanish"
     * 
     * @param cultureName The culture name to get the display name for
     */
    public getDisplayNameForCultureNameInDefaultLocale(cultureName: string): string {
        return AppServices.cultureNameToDefaultDisplayNameMap[cultureName] ?? "Unknown";
    }

    // ===============================================================================

    /**
     * Returns the localized display name of the given culture name,
     * localized to the user's current language.
     * 
     * @param cultureName The culture name to get the display name for, e.g. "en" or "es"
     */
    public getLocalizedDisplayNameForCultureName(cultureName: string): string {
        return this.localizeString(this.getDisplayNameForCultureNameInDefaultLocale(cultureName));
    }

    // ===============================================================================

    /**
     * Tracks an event in our cloud analytics service, Application Insights (on Azure).
     *
     * @param event the name of the event to track
     * @param context optional; any context data that goes with the event
     */
    public trackEvent(event: string, context?: any) {
        const isLoggedIn = this.userContext !== null;

        const isToxnotEmployee = isLoggedIn &&
            (this.userContext!.emailAddress.endsWith("@toxnot.com") ||
                (this.userContext!.emailAddress === "bill.hoffman@outlook.com") ||
                (this.userContext!.emailAddress === "ignag@hotmail.com"));

        if (this.config.inTestingMode || !this.config.isProductionServer || !this.config.isUatServer || isToxnotEmployee) {
            console.log(event + " " + JSON.stringify(context));
            return;
        }

        if (!appInsights) {
            return;
        }

        let subscriptionName = "";
        let isFre = false;

        if (this.userContext) {
            subscriptionName = this.userContext.isSubscriptionActive ? this.userContext.subscription.baseSubscriptionProduct.productName : "";
            isFre = this.userContext.isFre;
        }

        const eventProperties: any = {
            SubscriptionName: subscriptionName,
            IsFre: isFre
        };

        if (context) {
            eventProperties["Context"] = context;
        }

        appInsights.trackEvent({
            name: event,
            properties: eventProperties
        });
    }

    // ===============================================================================

    /**
     * Tracks the current URL path as a page view in our cloud analytics service, Application Insights (on Azure).
     */
    public trackCurrentUrlPath() {
        appInsights.trackPageView();
    }

    // ===============================================================================

    /**
     * Redirects the user to the Sign In page.
     */
    public static redirectToSignInPage() {
        window.location.href = "/Account/SignIn?urlReferrer=" + encodeURIComponent(window.location.pathname);
    }

    // ===============================================================================

    public static redirectToGetStarted() {
        window.location.href = "/Start"
    }

    // ===============================================================================

    private static readonly cultureNameToDefaultDisplayNameMap: Readonly<{ [locale: string]: string }> = {
        "en": "English",
        "zh": "Chinese",
        "fr": "French",
        "de": "German",
        "it": "Italian",
        "ja": "Japanese",
        "pt": "Portueguese",
        "es": "Spanish",
        "th": "Thai"
    };
}
